import { buildHelpRequestPayload } from '../helpRequest';
import { ReferenceProjectsMessageView } from './ReferenceProjectsMessage';
import { ReferenceProjectsSelectorView } from './SelectorView';
import { toWireData } from './buildReply';
import { parseReferenceProjectsOptions } from './parseOptions';
import { parseReferenceProjectsReply } from './parseReply';
import type { GenerativeUiDescriptor, GenerativeUiDisplayPayload } from '../types';
import type { ReferenceProjectsOptions } from './parseOptions';
import type { ReferenceProjectsReply } from './parseReply';

/**
 * Descriptor for the reference-projects generative-UI message type.
 *
 * See the KNOWN EXCEPTION note in ../types.ts: alone among descriptors, this one's selector
 * performs network calls and consumes React context. The descriptor itself stays a thin wiring
 * layer — the Apollo query lives in ReferenceProjectsPicker, behind React.lazy.
 */
function parseReferenceProjectsDisplayPayload(
    data: unknown
): GenerativeUiDisplayPayload<'reference-projects', ReferenceProjectsReply> | null {
    const reply = parseReferenceProjectsReply(data);

    if (!reply) {
        return null;
    }

    return { type: 'reference-projects', data: reply };
}

export const referenceProjectsDescriptor: GenerativeUiDescriptor<
    'reference-projects',
    ReferenceProjectsOptions,
    ReferenceProjectsReply
> = {
    type: 'reference-projects',
    parseReply: parseReferenceProjectsReply,
    parseDisplayPayload: parseReferenceProjectsDisplayPayload,
    MessageView: ReferenceProjectsMessageView,
    render: (data) => {
        const payload = parseReferenceProjectsDisplayPayload(data);

        if (!payload) {
            return null;
        }

        return <ReferenceProjectsMessageView data={payload.data} />;
    },
    parseOptions: parseReferenceProjectsOptions,
    renderSelector: (data, onSubmit) => {
        const options = parseReferenceProjectsOptions(data);

        if (!options) {
            return null;
        }

        return (
            <ReferenceProjectsSelectorView
                options={options}
                onSubmit={(reply) =>
                    onSubmit(
                        JSON.stringify({ type: 'reference-projects', data: toWireData(reply) })
                    )
                }
                // No options are named: the two choices are fixed yes/no wording this library owns,
                // not assistant-supplied labels, so there is nothing for the model to talk about by
                // name the way there is for style tiles.
                onRequestHelp={() => onSubmit(buildHelpRequestPayload('reference-projects'))}
            />
        );
    },
};
